import React, { useEffect, useState } from 'react'
import {
    CACHE_SIZE_UNLIMITED,
    doc,
    collection,
    getDocs,
    initializeFirestore,
    persistentLocalCache,
    connectFirestoreEmulator,
    persistentMultipleTabManager,
    Firestore,
} from 'firebase/firestore'
import { getAuth, connectAuthEmulator, Auth } from 'firebase/auth'
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions'
import { initializeApp } from 'firebase/app'
import { createRoot } from 'react-dom/client'
import { createStorage } from './storage'
import { createServices, setupServiceEventsListener } from './services'
import type { Services } from './services/types'
import type { StorageModules } from './storage/types'
import App from './App'
import { InMemoryErrorTrackerService } from '~/features/error-tracker/services/in-memory'
import { ConfigService } from '~/features/config/service/config'
import type { GlobalEvent } from './types/global-events'
import { isImporterRunning } from '~/features/email-importer/services/types'
import { EventEmitter } from '~common/utils/events'
import * as Sentry from '@sentry/react'
import { LocalStoragePersister } from './features/state-persistence/local-storage-persister'
import { PartitionedDexiePersister } from './features/state-persistence/partitioned-dexie-persister'
import { LocalDexieDB } from './storage/local-db'
import { FirebaseCloudFunctionsService } from './features/cloud-functions/services/firebase'
import { setupRealtimeInvoiceAndTransactionListeners } from '~/services/realtime-sync'
import {
    FirestoreBackupService,
    restoreFromFilePicker,
} from '~/features/backup/services/firestore-backup'
import { initI18n } from './i18n/config'
import { DocumentIdCreator } from '~common/storage/types'
import { LoadingScreen } from '~common/components/loading-screen'
import { ThemeProvider } from '~common/theme/theme-provider'

declare global {
    interface Window {
        __backup: {
            exportAllToZip: () => Promise<void>
            restoreFromFilePicker: () => Promise<void>
            wipeAllTables: () => Promise<void>
            wipeTable: (tableName: string) => Promise<void>
        }
        refreshMatchingSuggestions: () => Promise<void>
        __firestore?: Firestore
        __auth?: Auth
        findDuplicateAssociations: () => Promise<void>
    }
}

Sentry.init({
    dsn: 'https://b273bfc05eed28a9da4a2a622718395e@o138129.ingest.us.sentry.io/4510104621350912',
    sendDefaultPii: true,
})

const AppInitializer: React.FC = () => {
    const [initState, setInitState] = useState<{
        services: Services | null
        storageModules: StorageModules | null
        globalEvents: EventEmitter<GlobalEvent> | null
        isReady: boolean
        error?: string
    }>({
        services: null,
        storageModules: null,
        globalEvents: null,
        isReady: false,
    })

    useEffect(() => {
        async function initialize() {
            try {
                await initI18n()

                const useFirebaseEmulator =
                    import.meta.env.MODE === 'development' &&
                    !import.meta.env.VITE_SKIP_FIREBASE_EMULATOR

                const fbApp = initializeApp({
                    apiKey: 'AIzaSyD6XDknr_yfaP889yq71Bco-bZTwIpsqFM',
                    authDomain: 'taxing-desktop.firebaseapp.com',
                    projectId: 'taxing-desktop',
                    storageBucket: 'taxing-desktop.firebasestorage.app',
                    messagingSenderId: '209157995208',
                    appId: '1:209157995208:web:eaf753420f872c672283fd',
                })

                const auth = getAuth(fbApp)
                const functions = getFunctions(fbApp, 'us-central1')
                const db = initializeFirestore(fbApp, {
                    localCache: persistentLocalCache({
                        cacheSizeBytes: CACHE_SIZE_UNLIMITED,
                        tabManager: persistentMultipleTabManager(),
                    }),
                })

                if (useFirebaseEmulator) {
                    connectFirestoreEmulator(db, 'localhost', 8080)
                    connectAuthEmulator(auth, 'http://localhost:9099')
                    connectFunctionsEmulator(functions, 'localhost', 5001)
                }

                await auth.authStateReady()
                const localDb = new LocalDexieDB()

                const createDocumentId: DocumentIdCreator = (collectionName) =>
                    doc(collection(db, collectionName)).id

                const storage = createStorage({
                    initDb: () => db,
                    initLocalDb: () => localDb,
                    createDocumentId,
                    getCurrentUserId: () => auth.currentUser?.uid ?? null,
                })
                const configService = new ConfigService({})
                const globalEvents = new EventEmitter<GlobalEvent>()

                const extensionId = import.meta.env.VITE_EXTENSION_ID

                const services = createServices({
                    auth,
                    storage,
                    _functions: functions,
                    config: configService,
                    globalEvents: globalEvents,
                    errorTracker: new InMemoryErrorTrackerService({
                        shouldLog: true,
                        globalEvents,
                    }),
                    cloudFunctions: new FirebaseCloudFunctionsService({
                        functions,
                    }),
                    webExtensionApi: window.chrome,
                    webExtensionId: extensionId,
                    statePersisters: {
                        importer: new LocalStoragePersister({
                            localStorageKey:
                                configService.getConfig('searchProcessor')
                                    .stateFilePath,
                            expectedVersion: 1,
                        }),
                        invoiceQueue: new PartitionedDexiePersister({
                            db: localDb,
                            queueId:
                                configService.getConfig('invoiceProcessor')
                                    .pdfProcessingQueuePersistencePath,
                        }),
                    },
                })

                const unsubscribeServiceEvents =
                    setupServiceEventsListener(services)
                const unsubscribeRealtime =
                    setupRealtimeInvoiceAndTransactionListeners(db, services)

                if (import.meta.env.MODE === 'development') {
                    const backupService = new FirestoreBackupService({
                        db,
                        storageModules: storage.modules,
                        getCurrentUserId: () => auth.currentUser?.uid ?? null,
                    })

                    window.__backup = {
                        exportAllToZip: async () => {
                            const { blob, filename } =
                                await backupService.exportAllToZip()
                            const url = URL.createObjectURL(blob)
                            const a = document.createElement('a')
                            a.href = url
                            a.download = filename
                            a.click()
                            URL.revokeObjectURL(url)
                        },
                        restoreFromFilePicker: () =>
                            restoreFromFilePicker(backupService),
                        wipeAllTables: () => backupService.wipeAllTables(),
                        wipeTable: (tableName: string) =>
                            backupService.wipeTable(tableName),
                    }
                    window.__firestore = db
                    window.__auth = auth
                }

                // Handle beforeUnload event when importer is running
                const handleBeforeUnload = (event: BeforeUnloadEvent) => {
                    const importProgress =
                        services.cache.getKey('importProgress')
                    if (importProgress && isImporterRunning(importProgress)) {
                        event.preventDefault()
                        event.returnValue =
                            'The importer is running, are you sure you want to close this tab?'
                        return 'The importer is running, are you sure you want to close this tab?'
                    }
                }

                const handleUnload = async () => {
                    unsubscribeServiceEvents()
                    unsubscribeRealtime()
                    const importProgress =
                        services.cache.getKey('importProgress')
                    if (importProgress && isImporterRunning(importProgress)) {
                        // Pause the importer before the page unloads
                        await services.importer.pause()
                    }
                }

                window.addEventListener('beforeunload', handleBeforeUnload)
                window.addEventListener('unload', handleUnload)

                setInitState({
                    services,
                    storageModules: storage.modules,
                    globalEvents,
                    isReady: true,
                })
            } catch (err) {
                console.error('Failed to initialize app:', err)
                setInitState((prev) => ({
                    ...prev,
                    error: err instanceof Error ? err.message : String(err),
                }))
            }
        }

        initialize()
    }, [])

    if (initState.error) {
        return (
            <LoadingScreen
                message="Failed to initialize application"
                error={initState.error}
            />
        )
    }

    if (!initState.isReady) {
        return <LoadingScreen message="Initializing application..." />
    }

    return (
        <App
            services={initState.services!}
            storageModules={initState.storageModules!}
            globalEvents={initState.globalEvents!}
        />
    )
}

const container = document.getElementById('root') as HTMLElement
const root = createRoot(container)
root.render(
    <ThemeProvider defaultTheme="dark">
        <AppInitializer />
    </ThemeProvider>,
)
